import { useEffect, useState } from 'react';

import { templatesApi } from '@/shared/api/transcriptor/templatesApi';

import {
  buildChecklistRows,
  ChecklistPanelTab,
  DocumentTemplate,
  hasCompletedChecklist,
} from '@/entities/templates';

export const useRecordingChecklists = (
  schemaId: string | undefined,
): ChecklistPanelTab[] => {
  const [tabs, setTabs] = useState<ChecklistPanelTab[]>([]);

  useEffect(() => {
    if (!schemaId) {
      setTabs([]);
      return undefined;
    }

    const abortState = { cancelled: false };

    const load = async () => {
      try {
        const schema = await templatesApi.getEncounterSchema(schemaId);
        const seriesIds = [
          ...new Set(
            (schema.documents ?? [])
              .map((document) => document.template_series_id)
              .filter((seriesId): seriesId is string => Boolean(seriesId)),
          ),
        ];
        const templates = await Promise.all(
          seriesIds.map(async (seriesId) => {
            try {
              return await templatesApi.getDocumentTemplateBySeriesId(seriesId);
            } catch {
              return null;
            }
          }),
        );

        const nextTabs = templates
          .filter(
            (template): template is DocumentTemplate =>
              template !== null &&
              template.status !== 'deleted' &&
              hasCompletedChecklist(template),
          )
          .map((template) => ({
            id: template.series_id,
            label: template.tab_title || template.name,
            rows: buildChecklistRows(template),
          }))
          .filter((tab) => tab.rows.length > 0);

        if (!abortState.cancelled) {
          setTabs(nextTabs);
        }
      } catch {
        if (!abortState.cancelled) {
          setTabs([]);
        }
      }
    };

    load();

    return () => {
      abortState.cancelled = true;
    };
  }, [schemaId]);

  return tabs;
};
